import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MessageSquare, Cpu, Activity, BookOpen,
  Settings2, Trash2, Send, Globe, Pin, Plus, X, RefreshCw, DollarSign, Share2, Check
} from "lucide-react";
import { workforceApi } from "../../lib/workforce";
import { API } from "../../lib/api";
import BrainBadge from "../../components/workforce/BrainBadge";
import BrainSelector from "../../components/workforce/BrainSelector";
import Markdown from "../../components/Markdown";

const TABS = [
  { id: "chat",     label: "Chat",     icon: MessageSquare },
  { id: "brain",    label: "Brain",    icon: Cpu },
  { id: "memory",   label: "Memory",   icon: BookOpen },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "settings", label: "Settings", icon: Settings2 },
];

export default function WorkerWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [worker, setWorker] = useState(null);
  const [brainMap, setBrainMap] = useState({});
  const [tab, setTab] = useState("chat");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [w, b] = await Promise.all([workforceApi.getWorker(id), workforceApi.listBrains()]);
      setWorker(w);
      const map = {};
      (b.brains || []).forEach((x) => { map[x.id] = x; });
      setBrainMap(map);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load worker");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="brut-card p-6 max-w-md mx-auto">
          <div className="font-heading font-bold text-lg mb-2">Couldn't load worker</div>
          <div className="text-sm text-slate-600 mb-4">{error}</div>
          <Link to="/workforce" className="brut-btn brut-btn-outline"><ArrowLeft size={14} /> Back</Link>
        </div>
      </div>
    );
  }
  if (!worker) return <div className="p-8 text-center text-slate-400 italic">Loading worker…</div>;

  const brain = brainMap[(worker.brain || {}).brain_id];

  return (
    <div className="min-h-screen flex flex-col" data-testid={`worker-workspace-${id}`}>
      {/* Header */}
      <header className="border-b-2 border-slate-900 bg-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4 flex-wrap">
          <Link to="/workforce" className="text-slate-500 hover:text-slate-900" data-testid="workspace-back"><ArrowLeft size={18} strokeWidth={2.5} /></Link>
          <div className="w-14 h-14 rounded-2xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-3xl shrink-0">
            {worker.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-xl leading-tight truncate">{worker.name}</div>
            <div className="text-sm text-slate-600 truncate">{worker.title} · {worker.department}</div>
          </div>
          <div className="flex items-center gap-3">
            {brain && <BrainBadge brain={brain} showFamily />}
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cost</div>
              <div className="font-mono font-bold text-sm">${(worker.cost_total || 0).toFixed(4)}</div>
            </div>
            <ShareConvBtn workerId={worker.worker_id} />
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto flex gap-1 mt-3 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-t-lg font-heading font-bold text-sm border-b-4 transition-all ${
                  active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <Icon size={14} strokeWidth={2.5} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="flex-1 min-h-0">
        {tab === "chat"     && <ChatTab     worker={worker} brain={brain} brainMap={brainMap} onReload={load} />}
        {tab === "brain"    && <BrainTab    worker={worker} onReload={load} />}
        {tab === "memory"   && <MemoryTab   worker={worker} />}
        {tab === "activity" && <ActivityTab worker={worker} brainMap={brainMap} />}
        {tab === "settings" && <SettingsTab worker={worker} onReload={load} onDeleted={() => nav("/workforce", { replace: true })} />}
      </div>
    </div>
  );
}

/* ---------- CHAT TAB (streaming) ---------- */
function ChatTab({ worker, brain, brainMap, onReload }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [webSearch, setWebSearch] = useState(false);
  const abortRef = useRef(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await workforceApi.getMessages(worker.worker_id);
      setMessages(r.messages || []);
    } catch (err) {
      console.error("Failed to load worker messages:", err);
    }
  }, [worker.worker_id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streamBuf]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const prompt = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: prompt }]);
    setSending(true);
    setStreamBuf("");
    const token = localStorage.getItem("token");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/workforce/workers/${worker.worker_id}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: prompt, web_search: webSearch }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        // Budget-exceeded (402) or other error — read JSON error body
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch (parseErr) { /* body wasn't JSON — keep HTTP status */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", acc = "", brainUsed = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data:")) continue;
          const payload = ln.slice(5).trim();
          if (!payload || payload === "[DONE]") continue;
          let ev; try { ev = JSON.parse(payload); } catch { continue; }
          if (ev.type === "meta") brainUsed = ev.brain_id;
          else if (ev.type === "delta") { acc += ev.content; setStreamBuf(acc); }
          else if (ev.type === "error") { acc += `\n⚠️ ${ev.error}`; setStreamBuf(acc); }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: acc, brain_id: brainUsed }]);
      setStreamBuf("");
      onReload();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${e.message}`, error: true }]);
      setStreamBuf("");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-160px)]" data-testid="worker-chat-tab">
      <div className="flex-1 overflow-y-auto p-6" data-testid="worker-messages">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12 max-w-md mx-auto">
              <div className="text-5xl mb-3 float-y inline-block">{worker.avatar}</div>
              <div className="font-heading text-xl font-bold">Say hi to {worker.name}</div>
              <div className="text-sm text-slate-500 mt-1">{worker.description || worker.title}</div>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble key={i} msg={m} worker={worker} brainMap={brainMap} />
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="max-w-[85%] border-2 rounded-2xl rounded-bl-sm px-4 py-3 bg-slate-50 border-slate-300">
                {brain && <div className="mb-2"><BrainBadge brain={brain} size="sm" /></div>}
                {streamBuf ? <Markdown text={streamBuf} /> : (
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full dot-blink bg-slate-500" />
                    <span className="w-2 h-2 rounded-full dot-blink bg-slate-500" style={{ animationDelay: "0.2s" }} />
                    <span className="w-2 h-2 rounded-full dot-blink bg-slate-500" style={{ animationDelay: "0.4s" }} />
                  </div>
                )}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>
      <footer className="border-t-2 border-slate-900 bg-white p-4">
        <div className="max-w-3xl mx-auto">
          {(worker.tools || []).includes("web_search") && (
            <div className="mb-2">
              <button
                onClick={() => setWebSearch((w) => !w)}
                data-testid="worker-websearch-toggle"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-heading font-bold border-2 ${
                  webSearch ? "bg-emerald-500 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"
                }`}
              >
                <Globe size={12} strokeWidth={2.5} /> Web search {webSearch ? "ON" : "OFF"}
              </button>
            </div>
          )}
          <div className="flex items-end gap-3">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Message ${worker.name}…`}
              className="brut-input flex-1 resize-none max-h-32"
              style={{ minHeight: 48 }}
              data-testid="worker-chat-input"
            />
            {sending ? (
              <button onClick={() => abortRef.current?.abort()} className="brut-btn brut-btn-outline" data-testid="worker-chat-stop">Stop</button>
            ) : (
              <button onClick={send} disabled={!input.trim()} className="brut-btn" data-testid="worker-chat-send">
                <Send size={16} strokeWidth={2.5} /> Send
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

function ShareConvBtn({ workerId }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const api = (await import("../../lib/api")).default;
      const r = await api.post(`/workforce/workers/${workerId}/share-conversation`).then((x) => x.data);
      const url = `${window.location.origin}${r.url_path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || "Nothing to share yet — chat first!");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={share}
      disabled={busy}
      title="Share latest conversation as a public link"
      data-testid="share-conversation-btn"
      className="brut-btn brut-btn-outline text-xs py-1.5 px-3"
    >
      {copied ? <Check size={14} className="text-emerald-600" /> : <Share2 size={14} strokeWidth={2.5} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}


function MessageBubble({ msg, worker, brainMap }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-3 font-medium">{msg.content}</div>
      </div>
    );
  }
  const brain = brainMap[msg.brain_id];
  return (
    <div className="flex justify-start" data-testid="worker-msg-assistant">
      <div className="max-w-[85%] border-2 rounded-2xl rounded-bl-sm px-4 py-3 bg-white border-slate-300">
        <div className="flex items-center gap-2 mb-2 text-xs">
          <span className="text-lg leading-none">{worker.avatar}</span>
          <span className="font-heading font-bold">{worker.name}</span>
          {brain && <BrainBadge brain={brain} size="sm" />}
          {typeof msg.cost === "number" && msg.cost > 0 && (
            <span className="text-[10px] text-slate-400 font-mono">${msg.cost.toFixed(4)}</span>
          )}
        </div>
        <Markdown text={msg.content} />
      </div>
    </div>
  );
}

/* ---------- BRAIN TAB (swap) ---------- */
function BrainTab({ worker, onReload }) {
  const [selected, setSelected] = useState((worker.brain || {}).brain_id);
  const [temp, setTemp] = useState((worker.brain || {}).temperature ?? 0.7);
  const [maxTok, setMaxTok] = useState((worker.brain || {}).max_tokens ?? 1500);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelected((worker.brain || {}).brain_id);
    setTemp((worker.brain || {}).temperature ?? 0.7);
    setMaxTok((worker.brain || {}).max_tokens ?? 1500);
  }, [worker]);

  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      await workforceApi.swapBrain(worker.worker_id, selected, { temperature: temp, max_tokens: maxTok });
      setSaved(true);
      onReload();
      setTimeout(() => setSaved(false), 2500);
    } finally { setBusy(false); }
  };

  const changed = selected !== (worker.brain || {}).brain_id ||
                  temp !== (worker.brain || {}).temperature ||
                  maxTok !== (worker.brain || {}).max_tokens;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="worker-brain-tab">
      <div className="brut-card p-4 mb-4 bg-amber-50 text-sm flex items-start gap-3">
        <RefreshCw size={16} strokeWidth={2.5} className="text-amber-700 mt-0.5 shrink-0" />
        <span>
          <b>Swap the brain, keep the worker.</b> {worker.name}'s persona, memory, and history stay exactly as they are.
          Only the intelligence engine changes.
        </span>
      </div>
      <BrainSelector value={selected} onChange={setSelected} />
      <div className="grid grid-cols-2 gap-4 mt-6 border-t border-slate-200 pt-4">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Temperature: <span className="font-mono">{temp.toFixed(2)}</span></label>
          <input type="range" min="0" max="2" step="0.1" value={temp} onChange={(e) => setTemp(parseFloat(e.target.value))} className="w-full accent-slate-900" data-testid="brain-tab-temp" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Max tokens: <span className="font-mono">{maxTok}</span></label>
          <input type="range" min="256" max="4000" step="64" value={maxTok} onChange={(e) => setMaxTok(parseInt(e.target.value, 10))} className="w-full accent-slate-900" data-testid="brain-tab-maxtok" />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-6">
        <button onClick={save} disabled={!changed || busy} className="brut-btn" data-testid="brain-swap-save">
          {busy ? "Saving…" : "Save brain settings"}
        </button>
        {saved && <span className="text-sm text-emerald-600 font-bold" data-testid="brain-saved-toast">✓ Saved — memory & history preserved.</span>}
      </div>
    </div>
  );
}

/* ---------- MEMORY TAB ---------- */
function MemoryTab({ worker }) {
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await workforceApi.listMemory(worker.worker_id);
    setItems(r.memories || []);
  }, [worker.worker_id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      await workforceApi.addMemory(worker.worker_id, { kind: "note", content: content.trim(), pinned });
      setContent(""); setPinned(false);
      load();
    } finally { setBusy(false); }
  };
  const del = async (mid) => {
    await workforceApi.deleteMemory(worker.worker_id, mid);
    load();
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="worker-memory-tab">
      <div className="brut-card p-5 mb-5">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Teach {worker.name} something new</div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="e.g., Our target customers are procurement managers at Fortune 1000 companies…"
          rows={3}
          className="brut-input resize-none"
          data-testid="memory-input"
        />
        <div className="flex items-center justify-between mt-3">
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="accent-slate-900" data-testid="memory-pinned" />
            <Pin size={14} strokeWidth={2.5} /> Pin so it's always in context
          </label>
          <button onClick={add} disabled={busy || !content.trim()} className="brut-btn" data-testid="memory-add">
            <Plus size={14} strokeWidth={2.5} /> Add memory
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 text-slate-500 italic">No memories yet. Add some knowledge {worker.name} should always remember.</div>
      ) : (
        <div className="space-y-2" data-testid="memory-list">
          {items.map((m) => (
            <div key={m.memory_id} className="brut-card p-3 flex items-start gap-3">
              {m.pinned ? <Pin size={14} strokeWidth={2.5} className="text-amber-600 mt-1 shrink-0" /> : <span className="w-3.5 h-3.5 mt-1 shrink-0" />}
              <div className="flex-1 text-sm">{m.content}</div>
              <button onClick={() => del(m.memory_id)} data-testid={`memory-del-${m.memory_id}`} className="text-slate-400 hover:text-red-500">
                <X size={14} strokeWidth={3} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- ACTIVITY TAB ---------- */
function ActivityTab({ worker, brainMap }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    workforceApi.getWorkerActivity(worker.worker_id).then((r) => setItems(r.activity || []));
  }, [worker.worker_id]);
  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="worker-activity-tab">
      {items.length === 0 ? (
        <div className="text-center py-10 text-slate-500 italic">No activity yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.activity_id} className="brut-card p-3 flex items-start gap-3">
              <div className="text-xs text-slate-400 font-mono w-32 shrink-0">{new Date(a.created_at).toLocaleString()}</div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{a.kind.replace("_", " ")}</div>
                <div className="text-sm text-slate-800">{a.summary}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- SETTINGS TAB ---------- */
function SettingsTab({ worker, onReload, onDeleted }) {
  const [form, setForm] = useState({
    name: worker.name, title: worker.title, department: worker.department,
    avatar: worker.avatar, description: worker.description, mission: worker.mission,
    instructions: worker.instructions || {}, auto_brain: worker.auto_brain,
    auto_brain_priority: worker.auto_brain_priority || "balanced",
    budget_daily: worker.budget_daily ?? "",
    budget_monthly: worker.budget_monthly ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [tplName, setTplName] = useState(`${worker.name} template`);
  const [tplDesc, setTplDesc] = useState(worker.description || "");
  const [tplPublic, setTplPublic] = useState(false);
  const [tplSaved, setTplSaved] = useState(false);

  const patch = (o) => setForm((f) => ({ ...f, ...o }));

  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      const payload = { ...form };
      // Convert empty strings to null so backend clears them
      payload.budget_daily = payload.budget_daily === "" ? null : parseFloat(payload.budget_daily);
      payload.budget_monthly = payload.budget_monthly === "" ? null : parseFloat(payload.budget_monthly);
      await workforceApi.updateWorker(worker.worker_id, payload);
      setSaved(true); onReload();
      setTimeout(() => setSaved(false), 2500);
    } finally { setBusy(false); }
  };

  const saveAsTemplate = async () => {
    setBusy(true);
    try {
      await (await import("../../lib/api")).default.post(
        `/workforce/workers/${worker.worker_id}/save-as-template`,
        { name: tplName, description: tplDesc, public: tplPublic }
      );
      setTplSaved(true); setTemplateOpen(false);
      setTimeout(() => setTplSaved(false), 3000);
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${worker.name}? Memory and history will be lost.`)) return;
    await workforceApi.deleteWorker(worker.worker_id);
    onDeleted();
  };

  const dayPct = form.budget_daily ? Math.min(100, ((worker.cost_today || 0) / form.budget_daily) * 100) : 0;
  const monthPct = form.budget_monthly ? Math.min(100, ((worker.cost_month || 0) / form.budget_monthly) * 100) : 0;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="worker-settings-tab">
      <div className="brut-card p-5 space-y-3">
        <h3 className="font-heading text-lg font-bold">Identity</h3>
        <div className="flex gap-3">
          <input value={form.avatar} onChange={(e) => patch({ avatar: e.target.value })} className="brut-input w-20 text-center text-2xl" maxLength={4} data-testid="settings-avatar" />
          <input value={form.name} onChange={(e) => patch({ name: e.target.value })} className="brut-input flex-1" placeholder="Name" data-testid="settings-name" />
        </div>
        <input value={form.title} onChange={(e) => patch({ title: e.target.value })} className="brut-input" placeholder="Title" data-testid="settings-title" />
        <input value={form.department} onChange={(e) => patch({ department: e.target.value })} className="brut-input" placeholder="Department" data-testid="settings-department" />
        <textarea value={form.description} onChange={(e) => patch({ description: e.target.value })} className="brut-input resize-none" placeholder="Description" rows={2} data-testid="settings-description" />
        <textarea value={form.mission} onChange={(e) => patch({ mission: e.target.value })} className="brut-input resize-none" placeholder="Mission" rows={2} data-testid="settings-mission" />
      </div>

      <div className="brut-card p-5 space-y-3">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2"><DollarSign size={18} strokeWidth={2.5} /> Budget</h3>
        <p className="text-sm text-slate-600">Set spending caps. When a budget is hit, {worker.name} pauses until the cap resets or you raise it.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Daily cap ($)</label>
            <input type="number" min="0" step="0.01" value={form.budget_daily}
                   onChange={(e) => patch({ budget_daily: e.target.value })}
                   placeholder="No cap" className="brut-input font-mono" data-testid="budget-daily" />
            <div className="mt-2">
              <div className="text-[10px] font-bold text-slate-500 mb-1">Today: ${(worker.cost_today || 0).toFixed(4)}</div>
              {form.budget_daily && (
                <div className="h-2 rounded-full bg-slate-100 border border-slate-900 overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${dayPct}%`, backgroundColor: dayPct >= 90 ? "#EF4444" : "#10B981" }} />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Monthly cap ($)</label>
            <input type="number" min="0" step="0.01" value={form.budget_monthly}
                   onChange={(e) => patch({ budget_monthly: e.target.value })}
                   placeholder="No cap" className="brut-input font-mono" data-testid="budget-monthly" />
            <div className="mt-2">
              <div className="text-[10px] font-bold text-slate-500 mb-1">This month: ${(worker.cost_month || 0).toFixed(4)}</div>
              {form.budget_monthly && (
                <div className="h-2 rounded-full bg-slate-100 border border-slate-900 overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${monthPct}%`, backgroundColor: monthPct >= 90 ? "#EF4444" : "#10B981" }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="brut-card p-5 space-y-3">
        <h3 className="font-heading text-lg font-bold">Auto Brain</h3>
        <p className="text-sm text-slate-600">Let AiMedley pick the best brain automatically for each task.</p>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={form.auto_brain} onChange={(e) => patch({ auto_brain: e.target.checked })} className="accent-slate-900" data-testid="auto-brain-toggle" />
          Enable Auto Brain
        </label>
        {form.auto_brain && (
          <div className="flex gap-1.5 flex-wrap">
            {["balanced", "quality", "cost", "speed"].map((p) => (
              <button
                key={p}
                onClick={() => patch({ auto_brain_priority: p })}
                data-testid={`auto-brain-${p}`}
                className={`px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 ${
                  form.auto_brain_priority === p ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                }`}
              >
                Optimize for {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} className="brut-btn" data-testid="settings-save">{busy ? "Saving…" : "Save changes"}</button>
        {saved && <span className="text-sm text-emerald-600 font-bold">✓ Saved</span>}
      </div>

      <div className="brut-card p-5">
        <h3 className="font-heading text-lg font-bold mb-2">Save as template</h3>
        <p className="text-sm text-slate-600 mb-3">Reuse this worker's persona + instructions to create similar ones later — or share publicly.</p>
        {!templateOpen ? (
          <button onClick={() => setTemplateOpen(true)} className="brut-btn brut-btn-outline" data-testid="open-save-template">
            <Plus size={14} strokeWidth={2.5} /> Save as template
          </button>
        ) : (
          <div className="space-y-2">
            <input value={tplName} onChange={(e) => setTplName(e.target.value)} className="brut-input" placeholder="Template name" data-testid="tpl-name" />
            <input value={tplDesc} onChange={(e) => setTplDesc(e.target.value)} className="brut-input" placeholder="Description" data-testid="tpl-desc" />
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={tplPublic} onChange={(e) => setTplPublic(e.target.checked)} className="accent-slate-900" data-testid="tpl-public" />
              Make public (visible to other AiMedley users)
            </label>
            <div className="flex gap-2">
              <button onClick={() => setTemplateOpen(false)} className="brut-btn brut-btn-outline text-sm">Cancel</button>
              <button onClick={saveAsTemplate} disabled={busy || !tplName.trim()} className="brut-btn text-sm" data-testid="tpl-save">Save template</button>
            </div>
          </div>
        )}
        {tplSaved && <div className="mt-2 text-sm text-emerald-600 font-bold" data-testid="tpl-saved-toast">✓ Template saved!</div>}
      </div>

      <div className="brut-card p-5 border-red-500 border-2 bg-red-50">
        <h3 className="font-heading text-lg font-bold text-red-700 mb-1">Danger zone</h3>
        <p className="text-sm text-slate-700 mb-3">This permanently deletes the worker and everything they know.</p>
        <button onClick={del} className="brut-btn" style={{ backgroundColor: "#EF4444", borderColor: "#B91C1C" }} data-testid="settings-delete">
          <Trash2 size={14} strokeWidth={2.5} /> Delete worker
        </button>
      </div>
    </div>
  );
}
